import { Grid, Card, CardContent, CardHeader, Typography, Divider, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { SkeletonIndicator } from '../../../../components/skeleton-indicator.component';
import { Project } from '../../../../models/project';
import { ZIPCODE_FORMAT } from '../../../../constants';
import { formatValue } from '../../../../utils/data-format';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

interface ProjectInfoProps {
  project?: Project;
  isLoading?: boolean;
}

interface ComponentCell {
  isLoading: any;
  heading: string;
  value: any;
}

const styling = {
  root: { borderRadius: '10px' },
  textColor: { color: '$primary-color' },
  headingStyle: { color: '#0000b5', paddingBottom: '10px' },
  labelStyle: { color: '#0000b5', fontSize: '14px' },
  valueStyle: { color: '#000', fontWeight: '500', fontSize: '13px' },
};

const ComponentCell = ({ isLoading, heading, value }: ComponentCell) => {
  return (
    <Grid container spacing={0.5}>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        <Typography variant="body1" color="textPrimary" style={styling.labelStyle}>
          {heading}
        </Typography>
      </Grid>
      <Grid item lg={12} sm={12} xl={12} xs={12}>
        {!isLoading ? (
          <Typography variant="body1" color="text.secondary" style={styling.valueStyle}>
            {value ? value : '-----------'}
          </Typography>
        ) : (
          <SkeletonIndicator isLoading={isLoading} count={1} variant="h6" />
        )}
      </Grid>
    </Grid>
  );
};

export const ProjectInfo = ({ project, isLoading }: ProjectInfoProps) => {

  const isUATEnable = process.env.REACT_APP_UAT_FLAG === 'true';
  return (
    <Card elevation={0} sx={{ mr: 2 }}>
      <CardHeader
        className="subHead"
        title={
          <Typography variant="h3" color="textPrimary" style={styling.headingStyle}>
            PROJECT INFO
          </Typography>
        }
      />
      <Divider sx={{ borderBottomWidth: '2px' }} />
      <CardContent>
        <Grid container spacing={2} className="custBoxValues">
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Project Name" value={project?.projectName} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Customer" value={project?.customerName} />
          </Grid>

          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Project Code" value={project?.projectCode} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Builder Code" value={project?.builderCode} />
          </Grid>
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Fee Owner" value={project?.feeOwner} />
          </Grid>
          {isUATEnable && (
            <>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Forecast Start Date" value={project?.forecastStartDate} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Estimated Starts per Month" value={project?.estimatedStartsPerMonth} />
              </Grid>
            </>
          )}

          {
            project?.syncWithDux && (
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="DUX Synced" value={project?.isDuxSyncSuccess ? 'Success' : 'Fail'} />
              </Grid>
            )
          }
          <Grid item lg={6} sm={6} xl={6} xs={6}>
            <ComponentCell isLoading={isLoading} heading="Department" value={project?.departmentName} />
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Office" value={project?.officeName} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Project Type" value={project?.projectTypeName} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Created" value={project?.createdOnText} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell isLoading={isLoading} heading="Project Subtype" value={project?.subtypeNames} />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <ComponentCell
                  isLoading={isLoading}
                  heading="Rebate/Utility Company"
                  value={project?.rebateCompanyNames}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12}>
            <Divider />
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={3} sm={3} xl={3} xs={3}>
                <ComponentCell isLoading={isLoading} heading="City" value={project?.city} />
              </Grid>
              <Grid item lg={4} sm={4} xl={4} xs={4}>
                <ComponentCell isLoading={isLoading} heading="Municipalities" value={project?.muncipalityNames} />
              </Grid>
              <Grid item lg={3} sm={3} xl={3} xs={3}>
                <ComponentCell isLoading={isLoading} heading="State" value={project?.stateName} />
              </Grid>
              <Grid item lg={2} sm={2} xl={2} xs={2}>
                <ComponentCell
                  isLoading={isLoading}
                  heading="Zip Code"
                  value={formatValue(project?.zipcode?.toString() ?? '', ZIPCODE_FORMAT, '#').formattedValue}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid container spacing={1}>
              <Grid item lg={10} sm={10} xl={10} xs={10}>
                <ComponentCell isLoading={isLoading} heading="Plan Review" value={project?.planReviewNames} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
